/**
 * 动作解析器 - 用于解析组件配置中的动作字符串
 * 支持多种参数格式：@value, @dataSet, @inheritDataSet（DataSetInheritContext 上的 camelCase 键）、字符串、数字等
 */

export interface ParsedAction {
  actionName: string;
  arg: any;
}

const INHERIT_PLACEHOLDER_PREFIX = '@inherit';

/**
 * `@inheritDataSet` → inheritCtx['dataSet']；`@inheritId` → inheritCtx['id']。
 * 后缀为 PascalCase，按首字母小写转成 context 上的 camelCase 键名。
 */
function resolveInheritPlaceholder(
  trimmedArg: string,
  inheritCtx: Record<string, unknown> | null | undefined
): unknown {
  if (!inheritCtx || !trimmedArg.startsWith(INHERIT_PLACEHOLDER_PREFIX)) {
    return undefined;
  }
  if (trimmedArg.length <= INHERIT_PLACEHOLDER_PREFIX.length) {
    return undefined;
  }
  const suffix = trimmedArg.slice(INHERIT_PLACEHOLDER_PREFIX.length);
  const key = suffix.charAt(0).toLowerCase() + suffix.slice(1);
  return inheritCtx[key];
}

/**
 * 解析动作字符串的通用函数
 * @param actionString 动作字符串，如 "updateValue(@value, @dataSet)" 或 "submit"
 * @param contextValue 上下文值，通常是事件触发时的值
 * @param contextProps 上下文属性对象，用于解析 @ 开头的参数
 * @param inheritCtx DataSetInheritContext 值，用于解析 `@inherit*` 占位符（与 @dataSet 等独立）
 * @returns 解析后的动作对象
 */
export function parseAction(
  actionString: string | undefined,
  contextValue: any = null,
  contextProps: Record<string, any> = {},
  inheritCtx?: Record<string, unknown> | null
): ParsedAction {
  if (!actionString) {
    return { actionName: '', arg: null };
  }

  // 匹配函数名和参数部分，如 "updateValue(@value, @dataSet)" -> ["updateValue", "@value, @dataSet"]
  const match = actionString.match(/^([^(]+)\(([^)]*)\)$/);
  if (!match) {
    // 如果没有参数，直接返回动作名和上下文值
    return { actionName: actionString, arg: contextValue };
  }

  const actionName = match[1].trim();
  const argsString = match[2].trim();

  if (!argsString) {
    // 如果参数部分为空，返回动作名和上下文值
    return { actionName, arg: contextValue };
  }

  // 解析参数，支持多个参数用逗号分隔
  const args = argsString.split(',').map(arg => {
    const trimmedArg = arg.trim();

    // 处理 @value 特殊参数（上下文值）
    if (trimmedArg === '@value') {
      return contextValue;
    }

    // @inheritDataSet / @inheritIndex / @inheritId 等：从 DataSetInheritContext 解析
    if (trimmedArg.startsWith(INHERIT_PLACEHOLDER_PREFIX)) {
      if (trimmedArg.length > INHERIT_PLACEHOLDER_PREFIX.length) {
        return resolveInheritPlaceholder(trimmedArg, inheritCtx ?? null);
      }
    }

    // 处理 @ 开头的参数（从 contextProps 中获取）
    if (trimmedArg.startsWith('@')) {
      const dataSetKey = trimmedArg.substring(1);
      return contextProps[dataSetKey];
    }

    // 处理字符串参数（去掉引号）
    if ((trimmedArg.startsWith('"') && trimmedArg.endsWith('"')) || 
        (trimmedArg.startsWith("'") && trimmedArg.endsWith("'"))) {
      return trimmedArg.slice(1, -1);
    }

    // 处理数字参数
    if (!isNaN(Number(trimmedArg))) {
      return Number(trimmedArg);
    }

    // 其他情况直接返回
    return trimmedArg;
  });

  // 如果只有一个参数，直接返回该参数；否则返回数组
  const arg = args.length === 1 ? args[0] : args;

  return { actionName, arg };
}

/**
 * 解析变更动作的便捷函数
 * @param changeAction 变更动作字符串
 * @param value 变更的值
 * @param props 组件属性对象
 * @param inheritCtx DataSetInheritContext，用于 `@inherit*` 占位符
 * @returns 解析后的动作对象
 */
export function parseChangeAction(
  changeAction: string | undefined,
  value: any,
  props: Record<string, any> = {},
  inheritCtx?: Record<string, unknown> | null
): ParsedAction {
  return parseAction(changeAction, value, props, inheritCtx);
}

/**
 * 解析点击动作的便捷函数
 * @param clickAction 点击动作字符串
 * @param props 组件属性对象
 * @param inheritCtx DataSetInheritContext，用于 `@inherit*` 占位符
 * @returns 解析后的动作对象
 */
export function parseClickAction(
  clickAction: string | undefined,
  props: Record<string, any> = {},
  inheritCtx?: Record<string, unknown> | null
): ParsedAction {
  return parseAction(clickAction, null, props, inheritCtx);
}

/**
 * 解析查询动作的便捷函数
 * @param queryAction 查询动作字符串
 * @param props 组件属性对象
 * @param inheritCtx DataSetInheritContext，用于 `@inherit*` 占位符
 * @returns 解析后的动作对象
 */
export function parseQueryAction(
  queryAction: string | undefined,
  props: Record<string, any> = {},
  inheritCtx?: Record<string, unknown> | null
): ParsedAction {
  return parseAction(queryAction, null, props, inheritCtx);
}

/**
 * 创建命令对象的通用函数
 * @param actionName 动作名称
 * @param arg 动作参数
 * @returns 命令对象
 */
export function createCommand(actionName: string, arg: any): {
  actionName: string;
  arg: any;
  timestamp: number;
} {
  return {
    actionName,
    arg,
    timestamp: Date.now()
  };
}
